<template>
  <component
    :is="to ? RouterLink : 'button'"
    :to="to"
    class="block w-full text-left"
    @click.prevent="onClick?.()"
  >
    <slot name="prefix" />
    <div class="item-shell relative" :class="leadingVariant && 'max-lg:pt-8'">
      <div
        v-if="leadingVariant"
        class="leading-stripe pointer-events-none absolute max-lg:inset-x-0 max-lg:top-0 max-lg:h-10 max-lg:rounded-t-md lg:inset-y-0 lg:-left-6 lg:w-8 lg:rounded-l-md"
        :style="{
          background: `var(--${leadingVariant}-bg)`,
          '--leading-content': `var(--${leadingVariant}-content)`,
        }"
      />
      <div
        v-if="leadingVariant && (leadingIcon || leadingTooltip)"
        class="absolute max-lg:inset-x-0 max-lg:top-0 max-lg:h-8 lg:inset-y-0 lg:-left-6 lg:w-6"
        @click.prevent.stop
      >
        <Information v-if="leadingTooltip" class="leading-info !block size-full">
          <template #trigger>
            <div
              class="flex size-full items-center justify-center"
              :style="{ color: `var(--${leadingVariant}-content)` }"
            >
              <component :is="leadingIcon" v-if="leadingIcon" class="size-4" />
            </div>
          </template>
          <template #modal>
            <span class="whitespace-pre-line">{{ leadingTooltip }}</span>
          </template>
        </Information>
        <div
          v-else
          class="pointer-events-none flex size-full items-center justify-center"
          :style="{ color: `var(--${leadingVariant}-content)` }"
        >
          <component :is="leadingIcon" class="size-4" />
        </div>
      </div>
      <div
        class="item-container relative rounded-md"
        :style="`--bg-color: var(--${variant}-bg); --content-color: var(--${variant}-content); background: var(--bg-color);${leadingVariant ? ` box-shadow: inset 0 0 0 2.5px var(--${leadingVariant}-bg)` : ''}`"
      >
        <div class="flex px-3 py-2">
          <div class="flex flex-1 items-start gap-4 text-sm md:items-center">
            <slot />
          </div>
          <DropdownDotted
            v-if="dropDownItems?.length"
            :buttons="dropDownItems"
            class="max-sm:absolute max-sm:right-2 max-sm:top-2"
          />
        </div>
      </div>
    </div>
    <slot name="suffix" />
  </component>
</template>

<script lang="ts" setup>
import type { Component } from 'vue';
import { type RouteLocationRaw, RouterLink } from 'vue-router';
import DropdownDotted from '@/components/DropdownDotted.vue';
import Information from '@/components/Information.vue';

export type ListItemVariant =
  | 'default'
  | 'warning'
  | 'problem'
  | 'danger'
  | 'info'
  | 'success'
  | 'transition'
  | 'orange';

withDefaults(
  defineProps<{
    to?: RouteLocationRaw;
    onClick?: () => void;
    variant?: ListItemVariant;
    leadingVariant?: ListItemVariant;
    leadingIcon?: Component;
    leadingTooltip?: string;
    dropDownItems?: {
      title: string;
      onClick?: () => void;
    }[];
  }>(),
  { variant: 'default' },
);
</script>

<style scoped>
.leading-info :deep(> div),
.leading-info :deep(> div > div) {
  display: block !important;
  height: 100%;
  width: 100%;
}

.leading-info :deep(> div > div > div:first-child) {
  height: 100%;
}

.leading-stripe {
  box-shadow: inset 0 -30px 12px -12px color-mix(in srgb, var(--leading-content) 17%, transparent);
}

@media (min-width: 1024px) {
  .leading-stripe {
    box-shadow: inset -30px 0 12px -12px color-mix(in srgb, var(--leading-content) 17%, transparent);
  }
}

.item-shell {
  --default-bg: var(--color-card-default-bg);
  --default-content: var(--color-card-default-content);
  --success-bg: var(--color-card-success-bg);
  --success-content: var(--color-card-success-content);
  --info-bg: var(--color-card-info-bg);
  --info-content: var(--color-card-info-content);
  --transition-bg: linear-gradient(
    90deg,
    var(--color-card-success-bg) 0%,
    var(--color-card-info-bg) 100%
  );
  --transition-content: var(--color-card-success-content);
  --warning-bg: var(--color-card-warning-bg);
  --warning-content: var(--color-card-warning-content);
  --problem-bg: var(--color-card-problem-bg);
  --problem-content: var(--color-card-problem-content);
  --danger-bg: var(--color-card-danger-bg);
  --danger-content: var(--color-card-danger-content);
  --orange-bg: var(--color-card-orange-bg);
  --orange-content: var(--color-card-orange-content);
}
</style>
